
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Plaid Data Partner MCP',
  layout: 'guide',
  description: 'Quickstart for AI-assisted FDX diagnostics, validation, and integration health',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
      <p>{`The Plaid Data Partner MCP lets you use an AI agent to review production FDX request activity, troubleshoot individual calls, monitor integration health, and run and inspect validation for Core Exchange integrations.`}</p>
      <Callout mdxType="Callout">
  We're actively adding to this MCP, so if something doesn't work the way you
  expect, or there's a tool you wish existed, contact us.
      </Callout>
      <h2 {...{
        "id": "installation"
      }}>{`Installation`}</h2>
      <p>{`Use an MCP client that supports remote servers. The server URL is:`}</p>
      <pre><code parentName="pre" {...{}}>{`https://data-partner.mcp.plaid.com/mcp
`}</code></pre>
      <p>{`If your client asks for a transport, choose Streamable HTTP.`}</p>
      <p>{`If no team appears after you sign in, confirm that your Dashboard account is verified and belongs to the expected Data Partner team.`}</p>
      <h3 {...{
        "id": "claude-code"
      }}>{`Claude Code`}</h3>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`claude mcp add --transport http --scope user plaid-dp-mcp https://data-partner.mcp.plaid.com/mcp
`}</code></pre>
      <p>{`Run `}<inlineCode parentName="p">{`/mcp`}</inlineCode>{`, select `}<inlineCode parentName="p">{`plaid-dp-mcp`}</inlineCode>{`, and complete the browser sign-in. If you've already added the server as a connector in Claude, Claude Code loads it automatically when you sign in with the same account.`}</p>
      <h3 {...{
        "id": "claude-web-or-desktop"
      }}>{`Claude (web or desktop)`}</h3>
      <p>{`Go to `}<strong parentName="p">{`Customize`}</strong>{` → `}<strong parentName="p">{`Connectors`}</strong>{` → `}<strong parentName="p">{`Add custom connector`}</strong>{` and enter the server URL.`}</p>
      <p>{`On Team and Enterprise plans, an Owner must first add the URL under `}<strong parentName="p">{`Organization settings`}</strong>{` → `}<strong parentName="p">{`Connectors`}</strong>{`. See `}<a parentName="p" {...{
          "href": "https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp"
        }}>{`Claude's custom connector documentation`}</a>{`.`}</p>
      <h3 {...{
        "id": "codex-app-cli-or-ide"
      }}>{`Codex (app, CLI, or IDE)`}</h3>
      <p>{`In the app or IDE, open `}<strong parentName="p">{`Settings`}</strong>{` → `}<strong parentName="p">{`MCP servers`}</strong>{`, choose Streamable HTTP, and add the server URL. Or run:`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`codex mcp add plaid-dp-mcp --url https://data-partner.mcp.plaid.com/mcp
codex mcp login plaid-dp-mcp
`}</code></pre>
      <p>{`Restart the app or IDE if prompted, then complete the browser sign-in.`}</p>
      <p>{`Codex clients on the same computer share MCP configuration, so you only need to add the server once.`}</p>
      <h3 {...{
        "id": "chatgpt-web"
      }}>{`ChatGPT web`}</h3>
      <ol>
        <li parentName="ol">{`Go to `}<strong parentName="li">{`Settings`}</strong>{` → `}<strong parentName="li">{`Security and login`}</strong>{` → `}<strong parentName="li">{`Enable Developer mode`}</strong>{`.`}</li>
        <li parentName="ol">{`Go to chatgpt.com/plugins, select the `}<strong parentName="li">{`+`}</strong>{` button, and create a developer-mode app using the server URL.`}</li>
      </ol>
      <p>{`If you don't see `}<strong parentName="p">{`Developer mode`}</strong>{` under `}<strong parentName="p">{`Settings`}</strong>{` → `}<strong parentName="p">{`Security and login`}</strong>{`, your plan doesn't support adding MCP servers or your workspace admin hasn't enabled it.`}</p>
      <h2 {...{
        "id": "mcp-server-capabilities"
      }}>{`MCP server capabilities`}</h2>
      <p>{`All tools require that the logged-in user have the corresponding permission in the Dashboard. If you encounter an authentication error, check that you are logged in as the correct user and that you have the required permissions.`}</p>
      <h3 {...{
        "id": "diagnose-requests"
      }}>{`Diagnose requests`}</h3>
      <ul>
        <li parentName="ul">{`Search production FDX requests from the past 14 days by end user, endpoint, HTTP status, error state, or time range.`}</li>
        <li parentName="ul">{`Look up any request by ID to see its endpoint, status, latency, timestamp, and error details.`}</li>
      </ul>
      <p>{`Response bodies are available only for failed requests.`}</p>
      <p>{`Platform teams can search all their institutions or specific ones, and find institutions by name, ID, or integration status.`}</p>
      <p>{`Example prompts:`}</p>
      <ul>
        <li parentName="ul">{`Look up request `}<inlineCode parentName="li">{`0asd1a92nc`}</inlineCode>{`, explain what happened, and suggest the next check.`}</li>
        <li parentName="ul">{`User `}<inlineCode parentName="li">{`<user_id>`}</inlineCode>{` says balances are missing. Review their `}<inlineCode parentName="li">{`/accounts`}</inlineCode>{` requests from the past 24 hours and explain the likely cause.`}</li>
        <li parentName="ul">{`For Gingham Bank, review failed `}<inlineCode parentName="li">{`/accounts`}</inlineCode>{` requests from the past 24 hours and explain the likely cause.`}</li>
      </ul>
      <h3 {...{
        "id": "run-and-review-extraction-validation"
      }}>{`Run and review extraction validation`}</h3>
      <ul>
        <li parentName="ul">{`Start validation with an OAuth access token your institution issued for a test user.`}</li>
        <li parentName="ul">{`Single-institution teams can validate against production, staging, or development; platform teams can validate in production only.`}</li>
        <li parentName="ul">{`Review the results of recent validations, including scheduled ones, filtered by product or outcome.`}</li>
      </ul>
      <p>{`Example prompt:`}</p>
      <ul>
        <li parentName="ul">{`Run extraction validation in production with `}<inlineCode parentName="li">{`<oauth_access_token>`}</inlineCode>{`, then summarize failures and next steps.`}</li>
      </ul>
      <h3 {...{
        "id": "monitor-integration-health"
      }}>{`Monitor integration health`}</h3>
      <ul>
        <li parentName="ul">{`View successful and failed extractions from the past 14 days`}</li>
        <li parentName="ul">{`View the error rate over time`}</li>
        <li parentName="ul">{`View the most frequent extraction errors and whether each is new or increasing`}</li>
      </ul>
      <p>{`Platform results can be narrowed to specific institutions and show which institutions produced each error.`}</p>
      <p>{`Example prompt:`}</p>
      <ul>
        <li parentName="ul">{`Is my integration healthy? If error rates look elevated, show me what's failing.`}</li>
      </ul>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "installation",
        level: 2,
        title: "Installation",
      },
{
        id: "claude-code",
        level: 3,
        title: "Claude Code",
      },
{
        id: "claude-web-or-desktop",
        level: 3,
        title: "Claude (web or desktop)",
      },
{
        id: "codex-app-cli-or-ide",
        level: 3,
        title: "Codex (app, CLI, or IDE)",
      },
{
        id: "chatgpt-web",
        level: 3,
        title: "ChatGPT web",
      },
{
        id: "mcp-server-capabilities",
        level: 2,
        title: "MCP server capabilities",
      },
{
        id: "diagnose-requests",
        level: 3,
        title: "Diagnose requests",
      },
{
        id: "run-and-review-extraction-validation",
        level: 3,
        title: "Run and review extraction validation",
      },
{
        id: "monitor-integration-health",
        level: 3,
        title: "Monitor integration health",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
